import React, { useState, useEffect } from 'react';
import './ValidationModal.css';
import { searchContrat, searchCandidates, searchEntity } from '../services/api';

// ─── Valeurs prédéfinies (miroir des normalisations backend) ───────────────
const OPTIONS = {
  typeDocument:      ['Contrat', 'Quittance', 'Avenant', 'Appel_prime', 'RI'],
  quittancement:     ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Mensuelle', 'Unique', 'Biennale', 'Triennale', 'Décennale'],
  typeToiture:       ['Traditionnelle', 'Terrasse', 'Métallique', 'Végétale', 'Mixte'],
  usageBatiment:     ['Ensemble vertical', 'Ensemble horizontal', 'Habitation & bureau', 'Habitation & commerce', '100% bureau', '100% commerce', 'Garage'],
  commerce:          ['Pas de commerce', '< 25%', '25% < surfaces commerces < 50%', '> 50%', '100%'],
  zoneInondable:     ['Oui', 'Non'],
  quartierPrioritaire: ['Oui', 'Non'],
  altitude:          ['<1500', '>1500'],
  natureChauffage:   ['Individuel', 'Collectif'],
  mh:                ['Oui', 'Non'],
  tauxVide:          ['<= 25%', '<= 50%', '<= 75%', '> 75%', '100%'],
  // Contrat créé depuis un RI introuvable : quasi-systématiquement chez le concurrent
  // (si Dargency plaçait déjà ce contrat, le RI n'arriverait pas sans le contrat d'origine).
  contratDargencyConcurrence: ['Concurrent', 'Dargency'],
};

const MULTI_OPTIONS = {
  typeChauffage: [
    'Electrique', 'Gaz', 'Pompe à chaleur - Climatisation réversible ou non',
    'Poêle ou cheminée à Bois ou Granule', 'Photovoltaique', 'Fioul', 'Aucun', 'Autre'
  ],
  installationEquipement: [
    'Piscine exterieure', 'Piscine Interieure', 'Tennis / Padel / Squash',
    'Résidence de service', 'Résidence sénior', 'Résidence étudiantes',
    'SPA / jacuzzi / Sauna / hammam / salle de fitness', 'Résidence saisonnière',
    'Panneau Photovoltaique', 'Panneau Solaire'
  ],
  BrisMachine: [
    'Portail électrique', 'Ascenseur / monte charge', 'Pompe de relevage', 'Autre'
  ],
};

// ─── Module-level components ───────────────────────────────────────────────
// Déclarés EN DEHORS de tout composant → référence stable → pas de perte de focus.

function EditableField({ fieldKey, label, value, onChange, important }) {
  return (
    <div className={`vm-editable-field${important ? ' vm-field-important' : ''}`}>
      <label>
        {label}
        {important && <span className="vm-field-badge">⚠ clé</span>}
      </label>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        className={important ? 'vm-input-important' : ''}
      />
    </div>
  );
}

function SelectField({ fieldKey, label, value, onChange, options, important }) {
  return (
    <div className={`vm-editable-field${important ? ' vm-field-important' : ''}`}>
      <label>
        {label}
        {important && <span className="vm-field-badge">⚠ clé</span>}
      </label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        className={`vm-select${important ? ' vm-input-important' : ''}`}
      >
        <option value="">— choisir —</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

// Multi-select avec cases à cocher
function CheckGroup({ fieldKey, label, options, data, onChange }) {
  const values = Array.isArray(data[fieldKey]) ? data[fieldKey] : [];
  const toggle = (opt) => {
    const next = values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt];
    onChange(fieldKey, next);
  };
  return (
    <div className="vm-editable-field vm-check-group">
      <label>{label}</label>
      <div className="vm-check-options">
        {options.map(opt => (
          <label key={opt} className="vm-check-option">
            <input type="checkbox" checked={values.includes(opt)} onChange={() => toggle(opt)} />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function CG({ k, l, data, onChange }) {
  return <CheckGroup fieldKey={k} label={l} options={MULTI_OPTIONS[k]} data={data} onChange={onChange} />;
}

// Raccourcis stables (module level) pour le formulaire d'édition
function F({ k, l, imp, data, onChange }) {
  return (
    <EditableField fieldKey={k} label={l} value={data[k]} onChange={onChange} important={imp} />
  );
}

function SF({ k, l, imp, data, onChange }) {
  return (
    <SelectField fieldKey={k} label={l} value={data[k]} onChange={onChange} options={OPTIONS[k]} important={imp} />
  );
}

// ─── Helpers d'affichage ───────────────────────────────────────────────────
function docEmoji(type) {
  if (!type) return '📋';
  const t = type.toLowerCase();
  if (t === 'contrat') return '📄';
  if (t === 'quittance') return '🧾';
  if (t === 'contrat_avec_quittance') return '📋';
  if (t === 'appel_prime' || t === 'appel de prime') return '📨';
  if (t === 'avenant') return '📝';
  if (t === 'ri' || t === "relevé d'informations" || t === "releve d'informations" || t === 'relevé de sinistralité' || t === 'releve de sinistralite' || t === 'statistique sinistres') return '🛡️';
  return '📋';
}

// Détecte si un document est un Relevé d'Informations / Statistique Sinistres
function isRI(item) {
  const t = (item?.typeDocument || '').toLowerCase().trim();
  return t === 'ri' || t.includes('relev') || t.includes('sinistr') || t.includes('statistique');
}

// "JJ/MM/AAAA" ou "AAAA-MM-JJ" → Date (UTC), ou null si absente / invalide
function lireDate(s) {
  const txt = typeof s === 'string' ? s.trim() : '';
  let m = txt.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  let a, mo, j;
  if (m) { j = +m[1]; mo = +m[2]; a = +m[3]; }
  else if ((m = txt.match(/^(\d{4})-(\d{2})-(\d{2})$/))) { a = +m[1]; mo = +m[2]; j = +m[3]; }
  else return null;
  if (a < 1900) return null;
  const d = new Date(Date.UTC(a, mo - 1, j));
  return d.getUTCDate() === j && d.getUTCMonth() === mo - 1 ? d : null;
}

const lendemain = d => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1));

// Mois complets entre deux dates (la seconde exclue) : 01/01/2022 → 01/01/2025 = 36
function moisComplets(de, a) {
  const mois = (a.getUTCFullYear() - de.getUTCFullYear()) * 12 + (a.getUTCMonth() - de.getUTCMonth());
  return a.getUTCDate() < de.getUTCDate() ? mois - 1 : mois;
}

// Un relevé doit couvrir les 36 mois qui précèdent sa date d'édition (ou sa fin si elle est
// plus récente), dernier jour inclus. Une fin écrite peut précéder l'édition de moins de
// 3 mois (arrêté des comptes) ; au-delà, le relevé s'arrête trop tôt (ex. contrat résilié).
// → { ok, etat: 'ok'|'courte'|'arretee'|'inconnue'|'incoherente', mois, debut, fin, edition }
const MOIS_REQUIS_RI = 36;
const ECART_FIN_TOLERE_MOIS = 3;
function couvertureRI(item) {
  const debutTxt = item?.periodeStatistiqueDebut;
  const editionTxt = item?.dateStatistique;
  const finTxt = lireDate(item?.periodeStatistiqueFin) ? item.periodeStatistiqueFin : editionTxt;
  const debut = lireDate(debutTxt);
  const fin = lireDate(finTxt);
  const edition = lireDate(editionTxt);
  if (!debut || !fin) return { ok: false, etat: 'inconnue' };
  if (debut > fin) return { ok: false, etat: 'incoherente', debut: debutTxt, fin: finTxt };

  const reference = edition && edition > fin ? edition : fin;
  const ecartFin = moisComplets(lendemain(fin), lendemain(reference));
  if (ecartFin >= ECART_FIN_TOLERE_MOIS) {
    return { ok: false, etat: 'arretee', mois: ecartFin, debut: debutTxt, fin: finTxt, edition: editionTxt };
  }
  // Mois comptés jusqu'à la référence : c'est aussi la date affichée en fin de période
  const mois = moisComplets(debut, lendemain(reference));
  const referenceTxt = reference === fin ? finTxt : editionTxt;
  return { ok: mois >= MOIS_REQUIS_RI, etat: mois >= MOIS_REQUIS_RI ? 'ok' : 'courte', mois, debut: debutTxt, fin: referenceTxt };
}

// Phrase complète (bandeau, fiche) et libellé court (pastille de la colonne Période)
function texteCouvertureRI(c) {
  switch (c.etat) {
    case 'courte':      return `ne couvre que ${c.mois} mois sur les ${MOIS_REQUIS_RI} derniers (${c.debut} → ${c.fin})`;
    case 'arretee':     return `s'arrête le ${c.fin}, ${c.mois} mois avant son édition (${c.edition}) : les derniers mois ne sont pas couverts`;
    case 'incoherente': return `dates incohérentes, le début (${c.debut}) est après la fin (${c.fin})`;
    default:            return `période inconnue, impossible de vérifier les ${MOIS_REQUIS_RI} derniers mois`;
  }
}
function pastilleCouvertureRI(c) {
  switch (c.etat) {
    case 'courte':      return `${c.mois} mois < ${MOIS_REQUIS_RI}`;
    case 'arretee':     return `arrêté ${c.mois} mois avant l'édition`;
    case 'incoherente': return 'dates incohérentes';
    default:            return 'période inconnue';
  }
}

// Listes Airtable (miroir backend ; à garder sync avec packages/core/services/claudeVision.js)
const GARANTIES_RI = [
  'Accidents ménagers', 'Assistance', 'Attentats', 'Autre',
  'Bris de glaces', 'Bris de machines',
  'Catastrophe naturelle', 'Catastrophe technologique',
  'Choc de VTM', 'Décès', 'Dégât des eaux',
  'Détériorations immobilières', 'Dommages accidents', 'Dommages électriques',
  'Effondrement', 'Foudre', 'Incapacité, invalidité', 'Incendie',
  "Perte d'exploitation", 'Pertes pécuniaires diverses',
  'Protection juridique', 'Recours', 'Responsabilité civile', 'Santé',
  'Tempête, ouragan, cyclone, neige, grêle',
  'Vandalisme', 'Vol, tentative de vol'
];

const ETATS_RI = [
  'À vérifier', 'Pris en charge', 'En attente de documents', 'Vérification',
  'En cours', 'Sans suite', 'Refusé', 'Clôturé'
];

// ─── Main modal ────────────────────────────────────────────────────────────

// ─── [V2] Rapprochement avec l'existant ────────────────────────────────────
const MATCH_ENTITIES = [
  { key: 'immeuble', icon: '🏢', label: 'Immeuble' },
  { key: 'contrat',  icon: '📄', label: 'Contrat' },
  { key: 'client',   icon: '🏛️', label: 'Client lié' },
  { key: 'contact',  icon: '👤', label: 'Contact' },
];
const MATCH_STRONG = { immeuble: 80, contrat: 100, client: 85, contact: 85 };

// Une entité mérite l'attention humaine si : candidat fort (probable existant), OU
// pour l'immeuble un doute de proximité (même adresse / bâtiment différent / adresse proche).
// → en création PURE (aucun candidat pertinent), on ne propose rien.
function entityNeedsChoice(key, list) {
  if (!list || !list.length) return false;
  const top = list[0];
  if (top.score >= MATCH_STRONG[key]) return true;
  const f = top.flags || {};
  // Même résidence mais numéro de groupe différent (ex. doc « GROUPE 3 » vs candidat « G5 ») :
  // toujours montrer à l'humain (défaut = créer un nouveau) pour éviter un mauvais rapprochement.
  if (f.diffGroupe) return true;
  if (key === 'immeuble') {
    if (f.sameAddr || f.diffBatiment) return true;
    if (top.score >= 45) return true;
  }
  return false;
}

function matchFlagText(c) {
  const f = c.flags || {};
  if (f.diffGroupe) return ' ⚠️ groupe différent' + (f.candGrp ? ` (candidat ${f.candGrp})` : '');
  if (f.diffBatiment) return ' ⚠️ autre bâtiment';
  if (f.sameAddr) return ' ≈ même adresse';
  return '';
}

function RapprochementPanel({ doc, cands, sel, edit, open, loading, batchSiblings, onToggle, onPick, onEdit }) {
  if (loading && !cands) {
    return <div className="vm-match-panel vm-match-loading">🔎 Recherche de correspondances…</div>;
  }
  const c = cands || {};
  const s = sel || {};
  const ed = edit || {};
  const relevant = MATCH_ENTITIES.filter(e => entityNeedsChoice(e.key, c[e.key]));
  if (!relevant.length) {
    return (
      <div className="vm-match-panel vm-match-pure">
        🆕 Création pure — aucun rapprochement nécessaire{batchSiblings > 0 ? ` · 🔗 regroupé avec ${batchSiblings} autre(s) doc du même contrat` : ''}
      </div>
    );
  }
  // Vainqueur net = candidat fort ET nettement devant le 2e → pas de choix à faire (ligne confirmée).
  const clearWinner = (key, list) => {
    if (!list || !list.length || list[0].score < MATCH_STRONG[key]) return false;
    return list.length === 1 || list[1].score <= list[0].score - 15;
  };
  const chip = (e) => {
    const v = s[e.key];
    return `${e.icon} ${(!v || v === 'NEW') ? '➕' : '✓'}`;
  };
  return (
    <div className="vm-match-panel">
      <div className="vm-match-head">
        <span className="vm-match-title">🔗 Rapprochement avec l'existant</span>
        {batchSiblings > 0 && <span className="vm-match-batchnote">🔗 {batchSiblings} doc du même contrat dans ce lot</span>}
        {!open && (
          <span className="vm-match-summary">
            {relevant.map(e => <span key={e.key} className="vm-match-chip">{chip(e)}</span>)}
          </span>
        )}
        <button type="button" className="vm-match-toggle" onClick={() => onToggle(doc.id)}>
          {open ? 'Replier ▲' : 'Déplier ▼'}
        </button>
      </div>
      {open && (() => {
        const rows = relevant.map(ent => {
          const list = c[ent.key] || [];
          const v = s[ent.key];
          const inEdit = !!ed[ent.key];
          // Vert (confirmé) dès qu'un candidat EXISTANT est sélectionné — que ce soit
          // automatiquement (via contrat / via client / clé exacte) OU manuellement par clic.
          // Seul « Créer un nouveau » (NEW ou vide) reste un choix non confirmé.
          const confirmed = !inEdit && !!v && v !== 'NEW';
          return { ent, list, v, confirmed };
        });
        const okRows = rows.filter(r => r.confirmed);
        const chooseRows = rows.filter(r => !r.confirmed);
        return (
          <div className="vm-match-body">
            {okRows.length > 0 && (
              <div className="vm-confirmed-wrap">
                <div className="vm-cg-head">✓ Rapprochés automatiquement — clique ✎ pour changer ou créer</div>
                <div className="vm-confirmed-grid">
                  {okRows.map(({ ent, list, v }) => {
                    const chosen = list.find(x => x.recordId === v) || list[0];
                    return (
                      <React.Fragment key={ent.key}>
                        <span className="vm-cg-ent">{ent.icon} {ent.label}</span>
                        <span className="vm-cg-val" title={chosen.label}>{chosen.label}</span>
                        <span className="vm-cg-meta">
                          {chosen.flags && chosen.flags.viaContrat && <span className="vm-cg-via" title="Déduit du contrat (lien Airtable) — fiable">via contrat</span>}
                          <span className="vm-cg-score">{chosen.score >= 100 ? '✓' : chosen.score + '%'}</span>
                          <button type="button" className="vm-cg-edit" title="Changer ou créer un nouveau" onClick={() => onEdit(doc.id, ent.key)}>✎</button>
                        </span>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
            {chooseRows.map(({ ent, list, v }) => (
              <div key={ent.key} className="vm-match-entity vm-match-choose">
                <div className="vm-match-elabel">{ent.icon} {ent.label} <span className="vm-match-count">— choisis :</span></div>
                <div className="vm-cand-list">
                  {list.map(x => (
                    <label key={x.recordId} className={`vm-cand${v === x.recordId ? ' vm-cand-sel' : ''}`}>
                      <input type="radio" name={`${doc.id}-${ent.key}`} checked={v === x.recordId} onChange={() => onPick(doc.id, ent.key, x.recordId)} />
                      <span className="vm-cand-label">{x.label}</span>
                      <span className="vm-cand-meta"><b className="vm-match-scoreb">{x.score}</b>{matchFlagText(x)}</span>
                    </label>
                  ))}
                  <label className={`vm-cand vm-cand-new${(!v || v === 'NEW') ? ' vm-cand-sel' : ''}`}>
                    <input type="radio" name={`${doc.id}-${ent.key}`} checked={!v || v === 'NEW'} onChange={() => onPick(doc.id, ent.key, 'NEW')} />
                    <span className="vm-cand-label">➕ Créer un nouveau</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Recherche manuelle client/immeuble (fallback quand le rapprochement auto ne
// trouve rien — cas systématique pour les RI, qui n'extraient pas l'adresse) ───
function EntityPicker({ icon, label, selected, state, onQueryChange, onSearch, onPick, onChangeClick }) {
  if (selected) {
    return (
      <div style={{padding:'8px', background:'#dcfce7', borderRadius:'6px', fontSize:'13px'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'6px'}}>
          <span>{icon} <strong>{label}</strong> : {selected.label}</span>
          <button type="button" onClick={onChangeClick} style={{background:'none', border:'none', color:'#166534', cursor:'pointer', fontSize:'12px', textDecoration:'underline'}}>changer</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{padding:'8px', background:'#fff', border:'1px solid #e5e7eb', borderRadius:'6px'}}>
      <div style={{fontSize:'13px', fontWeight:600, marginBottom:'4px'}}>{icon} {label} <span style={{fontWeight:400, color:'#b45309'}}>— à sélectionner</span></div>
      <div style={{display:'flex', gap:'6px'}}>
        <input type="text" placeholder={`Chercher ${label.toLowerCase()}…`} value={state.query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearch(); } }}
          style={{flex:1, padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:'6px', fontSize:'13px'}} />
        <button type="button" className="vm-btn-secondary" onClick={onSearch} disabled={state.query.trim().length < 2 || state.status === 'loading'} style={{padding:'6px 12px'}}>
          {state.status === 'loading' ? '⏳' : '🔍'}
        </button>
      </div>
      {state.status === 'empty' && <div style={{marginTop:'6px', fontSize:'12px', color:'#b45309'}}>Aucun résultat.</div>}
      {state.candidates.length > 0 && (
        <div style={{marginTop:'6px'}}>
          {state.candidates.map(c => (
            <div key={c.id} onClick={() => onPick(c)}
              style={{padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:'6px', marginBottom:'4px', cursor:'pointer', fontSize:'12px', background:'#fafafa'}}
              onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}>
              <strong>{c.label}</strong>{c.sub ? ` — ${c.sub}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValidationModal({ data, onValidate, onClose }) {
  const [localData, setLocalData] = useState(data.map(d => ({ ...d })));
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  // Garde anti-double-clic : empêche d'envoyer 2 fois le batch (cause des doublons client/immeuble côté Airtable)
  const [isSubmitting, setIsSubmitting] = useState(false);
  // État de la recherche assistée de contrat (pour les RI)
  const [contratSearch, setContratSearch] = useState({ status: 'idle', candidates: [], message: '' });
  const [contratQuery, setContratQuery] = useState('');

  // [V2] Rapprochement : candidats existants par document
  const [matchData, setMatchData] = useState({});
  const [matchSel, setMatchSel] = useState({});
  const [matchOpen, setMatchOpen] = useState({});
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchEnabled, setMatchEnabled] = useState(true);   // toggle « Pipeline V2 »
  const [matchEdit, setMatchEdit] = useState({});           // entités passées en mode « changer »

  // Recherche manuelle client/immeuble (fallback, surtout pour les RI sans adresse extraite)
  const EMPTY_ENTITY_STATE = { query: '', status: 'idle', candidates: [] };
  const [entitySearch, setEntitySearch] = useState({ immeuble: EMPTY_ENTITY_STATE, client: EMPTY_ENTITY_STATE });

  const setEntityQuery = (type, query) => setEntitySearch(p => ({ ...p, [type]: { ...p[type], query } }));

  const runEntitySearch = async (type) => {
    const q = entitySearch[type].query;
    if (q.trim().length < 2) return;
    setEntitySearch(p => ({ ...p, [type]: { ...p[type], status: 'loading' } }));
    const candidates = await searchEntity(type, q);
    setEntitySearch(p => ({ ...p, [type]: { ...p[type], status: candidates.length ? 'results' : 'empty', candidates } }));
  };

  const pickEntity = (type, candidate) => {
    if (!editingId) return;
    const docId = editingId;
    const entry = [{ recordId: candidate.id, label: candidate.label, score: 100, flags: { manuel: true } }];
    setMatchData(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [type]: entry } }));
    setMatchSel(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [type]: candidate.id } }));
    setMatchEdit(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [type]: false } }));
    setMatchOpen(prev => ({ ...prev, [docId]: true }));
    setEntitySearch(p => ({ ...p, [type]: EMPTY_ENTITY_STATE }));
  };

  const clearEntity = (type) => {
    if (!editingId) return;
    const docId = editingId;
    setMatchSel(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [type]: null } }));
    setEntitySearch(p => ({ ...p, [type]: EMPTY_ENTITY_STATE }));
  };

  useEffect(() => {
    if (!matchEnabled || !localData.length) return;
    let cancelled = false;
    setMatchLoading(true);
    searchCandidates(localData)
      .then((cands) => {
        if (cancelled) return;
        const byId = {}, sel = {}, open = {};
        localData.forEach((doc, i) => {
          const c = cands[i] || {};
          byId[doc.id] = c;
          // Pré-sélection seulement si match CERTAIN (via contrat / clé exacte 100) ;
          // un match flou défaut sur « créer » (l'humain clique l'existant s'il le reconnaît).
          const pick = (k) => {
            const top = (c[k] || [])[0];
            const f = (top && top.flags) || {};
            // Pré-sélection auto si match certain : via contrat, via client (immeuble déduit du client matché), ou clé exacte.
            return (top && (f.viaContrat || f.viaClient || top.score >= 100)) ? top.recordId : 'NEW';
          };
          sel[doc.id] = { immeuble: pick('immeuble'), contrat: pick('contrat'), client: pick('client'), contact: pick('contact') };
          open[doc.id] = MATCH_ENTITIES.some(e => entityNeedsChoice(e.key, c[e.key]));
        });
        setMatchData(byId); setMatchSel(sel); setMatchOpen(open);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setMatchLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchEnabled]);

  const toggleMatch = (docId) => setMatchOpen(p => ({ ...p, [docId]: !p[docId] }));
  const editEntity = (docId, entity) =>
    setMatchEdit(p => ({ ...p, [docId]: { ...(p[docId] || {}), [entity]: true } }));
  const pickMatch = (docId, entity, value) => {
    setMatchSel(p => ({ ...p, [docId]: { ...(p[docId] || {}), [entity]: value } }));
    setMatchEdit(p => ({ ...p, [docId]: { ...(p[docId] || {}), [entity]: false } }));
  };

  const handleEdit = (doc) => {
    setEditingId(doc.id);
    setEditData({ contratDargencyConcurrence: 'Concurrent', ...doc });
    setContratSearch({ status: 'idle', candidates: [], message: '' });
    setContratQuery('');
    setEntitySearch({ immeuble: EMPTY_ENTITY_STATE, client: EMPTY_ENTITY_STATE });
    // Pour un RI : vérification silencieuse automatique du contrat à l'ouverture.
    // Affiche ✓ vert si trouvé, ou la recherche assistée seulement si introuvable.
    if (isRI(doc) && doc.numeroContrat) {
      autoVerifyContrat(doc.numeroContrat);
    }
  };

  // [V2] Quand la recherche assistée retrouve un contrat existant, remonter la chaîne :
  // réinjecter l'immeuble/client déjà liés à ce contrat dans le panneau « Rapprochement avec
  // l'existant », au lieu de le laisser figé sur son état initial (souvent « création pure »
  // quand le matching auto n'avait rien trouvé). Sans ça l'écran restait incohérent — contrat
  // trouvé, mais immeuble/client toujours affichés comme à créer — même si le backend, lui,
  // ne se trompait pas (il rematche par numéro indépendamment de ce panneau).
  const applyContratMatch = (candidate) => {
    if (!editingId || !candidate) return;
    const docId = editingId;
    const mk = (recordId, label) => (recordId ? [{ recordId, label: label || '(via contrat)', score: 100, flags: { viaContrat: true } }] : null);
    const contratCand = mk(candidate.id, candidate.numero);
    const immCand = mk(candidate.immeubleId, candidate.societe);
    const cliCand = mk(candidate.clientId, candidate.societe);
    setMatchData(prev => {
      const cur = prev[docId] || {};
      return { ...prev, [docId]: {
        ...cur,
        contrat: contratCand || cur.contrat,
        immeuble: immCand || cur.immeuble,
        client: cliCand || cur.client,
      } };
    });
    setMatchSel(prev => {
      const cur = prev[docId] || {};
      return { ...prev, [docId]: {
        ...cur,
        contrat: candidate.id || cur.contrat,
        immeuble: candidate.immeubleId || cur.immeuble,
        client: candidate.clientId || cur.client,
      } };
    });
    if (candidate.immeubleId || candidate.clientId) {
      setMatchOpen(prev => ({ ...prev, [docId]: true }));
    }
  };

  // Vérification silencieuse : appelée à l'ouverture d'un RI (pas de clic requis)
  const autoVerifyContrat = async (numero) => {
    setContratSearch({ status: 'loading', candidates: [], message: '' });
    try {
      const candidates = await searchContrat(numero, '');
      const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');
      const exact = candidates.find(c => norm(c.numero) === norm(numero));
      if (exact) {
        // Si la copro n'a pas été lue sur le PDF, on récupère le nom depuis le contrat Airtable
        // (utile pour classer le RI dans le bon dossier copro sous le syndic dans Drive).
        if (exact.societe) setEditData(prev => ({ ...prev, nomCopropriete: prev.nomCopropriete || exact.societe }));
        setContratSearch({ status: 'found', candidates: [], message: `Contrat trouvé : ${exact.societe || exact.numero}` });
        applyContratMatch(exact);
      } else if (candidates.length > 0) {
        setContratSearch({ status: 'candidates', candidates, message: `Contrat exact introuvable — ${candidates.length} proche(s), choisis le bon :` });
      } else {
        setContratSearch({ status: 'notfound', candidates: [], message: 'Contrat introuvable. Cherche par nom de copropriété ci-dessous.' });
      }
    } catch (e) {
      setContratSearch({ status: 'idle', candidates: [], message: '' });
    }
  };

  // Fermeture sans envoi : on renvoie l'état courant (localData) au parent pour que
  // les suppressions faites dans la modale soient persistées (sinon les documents
  // jetés réapparaissent à la réouverture).
  const handleClose = () => {
    // Inclure un éventuel edit en cours non enregistré
    const current = editingId
      ? localData.map(d => d.id === editingId ? { ...editData } : d)
      : localData;
    onClose(current);
  };

  // Vérifie le numéro de contrat / cherche des candidats dans Airtable
  const verifyContrat = async (byQuery) => {
    setContratSearch({ status: 'loading', candidates: [], message: '' });
    try {
      const numero = byQuery ? '' : (editData.numeroContrat || '');
      const query = byQuery ? contratQuery : '';
      const candidates = await searchContrat(numero, query);
      if (candidates.length === 0) {
        setContratSearch({ status: 'notfound', candidates: [], message: 'Aucun contrat trouvé. Essaie de chercher par nom de copropriété ci-dessous.' });
      } else {
        // match exact (numéro normalisé identique) ?
        const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');
        const exact = candidates.find(c => norm(c.numero) === norm(editData.numeroContrat));
        setContratSearch({
          status: exact ? 'found' : 'candidates',
          candidates,
          message: exact ? `Contrat trouvé : ${exact.societe || exact.numero}` : `${candidates.length} contrat(s) proche(s) — choisis le bon :`
        });
        if (exact) applyContratMatch(exact);
      }
    } catch (e) {
      setContratSearch({ status: 'notfound', candidates: [], message: 'Erreur de recherche : ' + e.message });
    }
  };

  const pickContrat = (candidate) => {
    // Cas « Créer ce contrat » coché : ce candidat proche (même copro, autre compagnie) sert
    // uniquement à récupérer le client/immeuble existants — on garde notre propre numéro
    // (celui du nouveau contrat à créer), on ne doit surtout pas l'écraser par le sien.
    if (editData.creerContratSiIntrouvable) {
      applyContratMatch(candidate);
      setContratSearch(prev => ({
        status: 'candidates',
        candidates: prev.candidates,
        message: `Client/immeuble récupérés depuis ${candidate.societe || candidate.numero} — numéro de contrat conservé : ${editData.numeroContrat || '(vide)'}`
      }));
      return;
    }
    // On adopte le numéro EXACT du contrat Airtable choisi → l'import le retrouvera sans ambiguïté.
    // On récupère aussi le nom de copro du contrat si le PDF ne l'a pas fourni (classement Drive).
    setEditData(prev => ({
      ...prev,
      numeroContrat: candidate.numero,
      nomCopropriete: prev.nomCopropriete || candidate.societe || prev.nomCopropriete
    }));
    setContratSearch({ status: 'found', candidates: [], message: `Contrat lié : ${candidate.societe || candidate.numero} (${candidate.numero})` });
    applyContratMatch(candidate);
  };

  const handleSave = () => {
    setLocalData(prev => prev.map(d => d.id === editingId ? { ...editData } : d));
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleDelete = (docId) => {
    if (window.confirm('Supprimer ce document de la validation ?')) {
      setLocalData(prev => prev.filter(d => d.id !== docId));
    }
  };

  const handleInputChange = (key, value) => {
    setEditData(prev => ({ ...prev, [key]: value }));
  };

  // ─── Handlers spécifiques aux RI / sinistres ──────────────────────────
  const addSinistre = () => {
    setEditData(prev => ({
      ...prev,
      statistiqueVierge: false,
      sinistres: [
        ...(prev.sinistres || []),
        {
          dateSinistre: '', numeroDossierCompagnie: '',
          garantie: '', etat: 'À vérifier',
          circonstances: '',
          evaluations: null, reglements: null, honoraires: null,
          franchise: null, recoursObtenu: null, coutTotal: null
        }
      ]
    }));
  };

  const removeSinistre = (idx) => {
    setEditData(prev => ({
      ...prev,
      sinistres: (prev.sinistres || []).filter((_, i) => i !== idx)
    }));
  };

  const updateSinistre = (idx, key, value) => {
    setEditData(prev => ({
      ...prev,
      sinistres: (prev.sinistres || []).map((s, i) => i === idx ? { ...s, [key]: value } : s)
    }));
  };

  const toggleVierge = (checked) => {
    setEditData(prev => ({
      ...prev,
      statistiqueVierge: checked,
      sinistres: checked ? [] : (prev.sinistres || [])
    }));
  };

  const handleValidate = async () => {
    if (isSubmitting) return; // garde anti-double-clic
    setIsSubmitting(true);
    try {
      let dataToSend = editingId
        ? localData.map(d => d.id === editingId ? { ...editData } : d)
        : localData;

      // [V2] Joindre les choix de rapprochement (recordId existant, ou null = créer)
      if (matchEnabled) {
        const idOrNull = (v) => (v && v !== 'NEW') ? v : null;
        dataToSend = dataToSend.map(d => {
          const s = matchSel[d.id] || {};
          return {
            ...d,
            matchImmeubleId: idOrNull(s.immeuble),
            matchContratId:  idOrNull(s.contrat),
            matchClientId:   idOrNull(s.client),
            matchContactId:  idOrNull(s.contact),
          };
        });
      }
      // Persiste le state si un edit était en cours, pour que la modale soit cohérente
      // si l'utilisateur réessaie (erreur réseau, etc.)
      if (editingId) {
        setLocalData(dataToSend);
        setEditingId(null);
        setEditData({});
      }
      // Debug : afficher un résumé visible dans la console browser
      const summary = dataToSend.map(d => isRI(d)
        ? { id: d.id, type: 'RI', contrat: d.numeroContrat, vierge: !!d.statistiqueVierge, nbSinistres: (d.sinistres || []).length }
        : { id: d.id, type: d.typeDocument, contrat: d.numeroContrat, primeTTC: d.primeTTC, primeAnnuelle: d.primeAnnuelle });
      console.log('[ValidationModal] Envoi', dataToSend.length, 'doc(s) :', summary);
      await onValidate(dataToSend);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vm-overlay" onClick={handleClose}>
      <div className="vm-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="vm-header">
          <div className="vm-header-left">
            <span className="vm-header-icon">📋</span>
            <div>
              <h2 className="vm-header-title">Révision avant envoi</h2>
              <p className="vm-header-sub">{localData.length} document{localData.length > 1 ? 's' : ''} extrait{localData.length > 1 ? 's' : ''} — Vérifiez et corrigez avant envoi vers Airtable</p>
            </div>
          </div>
          <label className="vm-v2-toggle" title="Proposer le rapprochement avec des enregistrements Airtable existants (immeuble / contrat / client / contact)">
            <input type="checkbox" checked={matchEnabled} onChange={(e) => setMatchEnabled(e.target.checked)} />
            <span>Pipeline V2 · rapprochement</span>
          </label>
          <button className="vm-close-btn" onClick={handleClose} title="Fermer sans envoyer">×</button>
        </div>

        {/* Alerte champs clés */}
        <div className="vm-key-alert">
          <span className="vm-key-alert-icon">⚠️</span>
          <span>Portez une attention particulière aux champs marqués <strong>⚠ clé</strong> : <strong>N° Contrat</strong>, <strong>Adresse &amp; CP &amp; Ville immeuble</strong>, <strong>Adresse syndic</strong> et <strong>Nom agent/courtier</strong> — ils sont utilisés par n8n pour le regroupement automatique.</span>
        </div>

        {/* Alerte relevés qui ne couvrent pas les 36 derniers mois */}
        {(() => {
          const courts = localData.filter(isRI).map(d => ({ d, c: couvertureRI(d) })).filter(x => !x.c.ok);
          if (courts.length === 0) return null;
          const n = courts.length;
          const tousMesures = courts.every(x => x.c.etat === 'courte' || x.c.etat === 'arretee');
          const titre = tousMesures
            ? (n > 1 ? `${n} relevés de sinistralité ne couvrent pas` : 'Ce relevé de sinistralité ne couvre pas') + ` les ${MOIS_REQUIS_RI} derniers mois`
            : `Couverture des ${MOIS_REQUIS_RI} derniers mois à vérifier sur ${n > 1 ? `${n} relevés` : 'ce relevé'} de sinistralité`;
          return (
            <div className="vm-ri-alert" role="alert" tabIndex={0} aria-labelledby="vm-ri-alert-titre">
              <span className="vm-key-alert-icon" aria-hidden="true">🛡️</span>
              <div>
                <strong id="vm-ri-alert-titre">{titre}</strong>
                <ul>
                  {courts.map(({ d, c }) => (
                    <li key={d.id}>
                      {String(d.nomCopropriete || d.metadata?.filename || 'Relevé')}
                      {d.numeroContrat ? ` (${d.numeroContrat})` : ''} : {texteCouvertureRI(c)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })()}

        {/* Body — table */}
        <div className="vm-body">
          {localData.length === 0 ? (
            <div className="vm-empty">Aucun document à valider.</div>
          ) : (
            <div className="vm-table-wrapper">
              <table className="vm-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Copropriété</th>
                    <th>Assureur</th>
                    <th>N° Contrat ⚠</th>
                    <th>Prime TTC / Sinistres</th>
                    <th>Période</th>
                    <th>Fichier</th>
                    <th className="vm-th-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {localData.map((item) => (
                    <React.Fragment key={item.id}>
                    <tr className="vm-tr-clickable" onClick={() => handleEdit(item)} title="Cliquer pour modifier">
                      <td>
                        <span className={`type-badge ${(item.typeDocument || '').toLowerCase()}`}>
                          {docEmoji(item.typeDocument)}
                          {item.typeDocument || 'N/A'}
                        </span>
                      </td>
                      <td>{item.nomCopropriete || item.adresseImmeuble || '-'}</td>
                      <td><span className="vm-assureur">{item.assureur || '-'}</span></td>
                      <td className="vm-code">{item.numeroContrat || <span className="vm-missing">manquant</span>}</td>
                      <td className="vm-amount">
                        {isRI(item)
                          ? (item.statistiqueVierge
                              ? <span style={{color:'#16a34a',fontWeight:600}}>Vierge</span>
                              : <span>🛡️ {(item.sinistres || []).length} sinistre{(item.sinistres || []).length > 1 ? 's' : ''}</span>)
                          : (item.primeTTC ? `${Number(item.primeTTC).toLocaleString('fr-FR')} €` : '-')}
                      </td>
                      <td>
                        {isRI(item)
                          ? <>
                              {`${item.periodeStatistiqueDebut || '?'} → ${item.periodeStatistiqueFin
                                || (item.dateStatistique ? `${item.dateStatistique} (édition)` : '?')}`}
                              {(() => {
                                const c = couvertureRI(item);
                                return !c.ok && (
                                  <span className="vm-badge-36" title={texteCouvertureRI(c)}>
                                    ⚠ {pastilleCouvertureRI(c)}
                                  </span>
                                );
                              })()}
                            </>
                          : `${item.periodeDebut || '-'} ${item.periodeFin ? '→ ' + item.periodeFin : ''}`}
                      </td>
                      <td className="vm-file" title={item.metadata?.filename}>
                        {item.metadata?.filename?.substring(0, 18) || '-'}
                        {item.metadata?.filename?.length > 18 && '…'}
                      </td>
                      <td className="vm-td-actions">
                        <button className="vm-btn-delete" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} title="Supprimer">🗑️</button>
                      </td>
                    </tr>
                    {matchEnabled && (
                      <tr className="vm-match-row">
                        <td colSpan={8}>
                          <RapprochementPanel
                            doc={item}
                            cands={matchData[item.id]}
                            sel={matchSel[item.id]}
                            open={!!matchOpen[item.id]}
                            loading={matchLoading}
                            edit={matchEdit[item.id]}
                            batchSiblings={localData.filter(d => d.numeroContrat && item.numeroContrat && String(d.numeroContrat).trim() === String(item.numeroContrat).trim()).length - 1}
                            onToggle={toggleMatch}
                            onPick={pickMatch}
                            onEdit={editEntity}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="vm-footer">
          <button className="vm-btn-secondary" onClick={handleClose}>
            Fermer sans envoyer
          </button>
          <button className="vm-btn-primary" onClick={handleValidate} disabled={localData.length === 0 || isSubmitting}>
            {isSubmitting ? `⏳ Envoi en cours (${localData.length})…` : `✅ Valider et envoyer (${localData.length})`}
          </button>
        </div>

        {/* Modal d'édition inline — TOUS les champs */}
        {editingId && (
          <div className="vm-edit-overlay" onClick={handleCancel}>
            <div className="vm-edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="vm-edit-header">
                <h3>Modifier le document</h3>
                <button className="vm-close-btn" onClick={handleCancel}>×</button>
              </div>

              <div className="vm-edit-body">

                {/* Section Informations générales (commune à tous les types) */}
                <div className="vm-edit-section">
                  <h4>Informations générales</h4>
                  <div className="vm-edit-grid">
                    <SF k="typeDocument" l="Type Document" data={editData} onChange={handleInputChange} />
                    <F  k="assureur" l="Assureur" data={editData} onChange={handleInputChange} />
                    <F  k="numeroContrat" l="N° Contrat" imp data={editData} onChange={handleInputChange} />
                  </div>
                </div>

                {/* ─── Section dédiée RI / Statistique sinistres (typeDocument = RI) ─── */}
                {isRI(editData) && (
                  <div className="vm-edit-section" style={{background:'#fff7ed', borderLeft:'4px solid #ea580c', paddingLeft:'12px'}}>
                    <h4>🛡️ Relevé d'Informations — Période & sinistres</h4>
                    <div className="vm-edit-grid">
                      <F k="dateStatistique" l="Date d'édition du relevé" imp data={editData} onChange={handleInputChange} />
                      <F k="periodeStatistiqueDebut" l="Période début" data={editData} onChange={handleInputChange} />
                      <F k="periodeStatistiqueFin" l="Période fin" data={editData} onChange={handleInputChange} />
                      <F k="nomCopropriete" l="Nom copropriété" data={editData} onChange={handleInputChange} />
                      <F k="adresseImmeuble" l="Adresse immeuble" data={editData} onChange={handleInputChange} />
                    </div>
                    {(() => {
                      const c = couvertureRI(editData);
                      return !c.ok && (
                        <p className="vm-ri-alert-inline">
                          ⚠ Relevé à vérifier : {texteCouvertureRI(c)}.
                        </p>
                      );
                    })()}

                    {/* ── Vérification / recherche assistée du contrat ── */}
                    <div style={{marginTop:'12px', padding:'10px', background:'#fff', border:'1px dashed #d1d5db', borderRadius:'8px'}}>
                      <div style={{display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap'}}>
                        <button type="button" className="vm-btn-secondary" onClick={() => verifyContrat(false)} disabled={contratSearch.status === 'loading' || !editData.numeroContrat} style={{padding:'6px 12px'}}>
                          {contratSearch.status === 'loading' ? '⏳ Recherche…' : '🔍 Vérifier le contrat dans Airtable'}
                        </button>
                        <span style={{fontSize:'12px', color:'#6b7280'}}>N° : <strong>{editData.numeroContrat || '(vide)'}</strong></span>
                      </div>

                      {contratSearch.status === 'found' && (
                        <div style={{marginTop:'8px', padding:'8px', background:'#dcfce7', borderRadius:'6px', color:'#166534', fontSize:'13px', fontWeight:600}}>
                          ✓ {contratSearch.message}
                        </div>
                      )}

                      {(contratSearch.status === 'candidates' || contratSearch.status === 'notfound') && (
                        <div style={{marginTop:'8px'}}>
                          <div style={{color:'#b45309', fontSize:'13px', marginBottom:'6px'}}>⚠️ {contratSearch.message}</div>
                          {contratSearch.candidates.map((c) => (
                            <div key={c.id} onClick={() => pickContrat(c)}
                              style={{padding:'8px', border:'1px solid #e5e7eb', borderRadius:'6px', marginBottom:'4px', cursor:'pointer', fontSize:'13px', background:'#fafafa'}}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                              onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}>
                              <strong>{c.numero || '(sans numéro)'}</strong>
                              {c.societe ? ` — ${c.societe}` : ''}{c.compagnie ? ` · ${c.compagnie}` : ''}
                            </div>
                          ))}
                          <div style={{display:'flex', gap:'6px', marginTop:'8px'}}>
                            <input type="text" placeholder="Chercher par nom de copropriété…" value={contratQuery}
                              onChange={(e) => setContratQuery(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verifyContrat(true); } }}
                              style={{flex:1, padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:'6px', fontSize:'13px'}} />
                            <button type="button" className="vm-btn-secondary" onClick={() => verifyContrat(true)} disabled={contratQuery.length < 3} style={{padding:'6px 12px'}}>Chercher</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── Créer un contrat minimal si introuvable (syndics bénévoles sans contrat transmis) ── */}
                    {(contratSearch.status === 'notfound' || contratSearch.status === 'candidates') && (
                      <div style={{marginTop:'12px', padding:'10px', background:'#eff6ff', border:'1px dashed #93c5fd', borderRadius:'8px'}}>
                        <label style={{display:'flex', alignItems:'flex-start', gap:'8px', cursor:'pointer'}}>
                          <input
                            type="checkbox"
                            checked={!!editData.creerContratSiIntrouvable}
                            onChange={(e) => handleInputChange('creerContratSiIntrouvable', e.target.checked)}
                            style={{marginTop:'3px'}}
                          />
                          <span style={{fontSize:'13px'}}>
                            <strong>🆕 Créer ce contrat</strong> — n° <strong>{editData.numeroContrat || '(vide)'}</strong> + compagnie ci-dessous, rattaché au client et à l'immeuble choisis ci-dessous. Les autres champs du contrat seront complétés plus tard.
                          </span>
                        </label>
                        {editData.creerContratSiIntrouvable && (
                          <div style={{marginTop:'8px'}}>
                            <F k="assureur" l="Compagnie" imp data={editData} onChange={handleInputChange} />
                            <SF k="contratDargencyConcurrence" l="Contrat Dargency ou Concurrence" imp data={editData} onChange={handleInputChange} />
                            {matchEnabled && (
                              <div style={{marginTop:'8px'}}>
                                <RapprochementPanel
                                  doc={editData}
                                  cands={matchData[editingId]}
                                  sel={matchSel[editingId]}
                                  open={true}
                                  loading={matchLoading}
                                  edit={matchEdit[editingId]}
                                  batchSiblings={localData.filter(d => d.id !== editingId && d.numeroContrat && editData.numeroContrat && String(d.numeroContrat).trim() === String(editData.numeroContrat).trim()).length}
                                  onToggle={toggleMatch}
                                  onPick={pickMatch}
                                  onEdit={editEntity}
                                />
                                {/* Recherche manuelle : indispensable pour un RI, le rapprochement auto
                                    ci-dessus ne trouve quasiment jamais rien (pas d'adresse extraite). */}
                                <div style={{marginTop:'8px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px'}}>
                                  <EntityPicker icon="🏢" label="Immeuble"
                                    selected={(() => {
                                      const v = matchSel[editingId]?.immeuble;
                                      if (!v || v === 'NEW') return null;
                                      return (matchData[editingId]?.immeuble || []).find(x => x.recordId === v) || { recordId: v, label: v };
                                    })()}
                                    state={entitySearch.immeuble}
                                    onQueryChange={(q) => setEntityQuery('immeuble', q)}
                                    onSearch={() => runEntitySearch('immeuble')}
                                    onPick={(c) => pickEntity('immeuble', c)}
                                    onChangeClick={() => clearEntity('immeuble')}
                                  />
                                  <EntityPicker icon="🏛️" label="Client lié"
                                    selected={(() => {
                                      const v = matchSel[editingId]?.client;
                                      if (!v || v === 'NEW') return null;
                                      return (matchData[editingId]?.client || []).find(x => x.recordId === v) || { recordId: v, label: v };
                                    })()}
                                    state={entitySearch.client}
                                    onQueryChange={(q) => setEntityQuery('client', q)}
                                    onSearch={() => runEntitySearch('client')}
                                    onPick={(c) => pickEntity('client', c)}
                                    onChangeClick={() => clearEntity('client')}
                                  />
                                </div>
                                {!matchSel[editingId]?.client && !matchSel[editingId]?.immeuble && (
                                  <div style={{marginTop:'6px', fontSize:'12px', color:'#b45309'}}>
                                    ⚠️ Sans client et immeuble existants sélectionnés ci-dessus, le contrat ne sera pas créé.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <label style={{display:'flex', alignItems:'center', gap:'8px', marginTop:'12px', padding:'8px', background:'#fef3c7', borderRadius:'6px', cursor:'pointer'}}>
                      <input
                        type="checkbox"
                        checked={!!editData.statistiqueVierge}
                        onChange={(e) => toggleVierge(e.target.checked)}
                      />
                      <span style={{fontWeight:600}}>Statistique vierge (aucun sinistre déclaré sur la période)</span>
                    </label>

                    {!editData.statistiqueVierge && (
                      <div style={{marginTop:'16px'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                          <strong>Sinistres ({(editData.sinistres || []).length})</strong>
                          <button type="button" className="vm-btn-secondary" onClick={addSinistre} style={{padding:'6px 12px'}}>
                            ➕ Ajouter un sinistre
                          </button>
                        </div>

                        {(editData.sinistres || []).length === 0 && (
                          <div style={{padding:'12px', background:'#f3f4f6', borderRadius:'6px', color:'#6b7280', fontSize:'13px', textAlign:'center'}}>
                            Aucun sinistre. Coche "Statistique vierge" ci-dessus, ou ajoute un sinistre manuellement.
                          </div>
                        )}

                        {(editData.sinistres || []).map((s, idx) => (
                          <div key={idx} style={{border:'1px solid #e5e7eb', borderRadius:'8px', padding:'12px', marginBottom:'10px', background:'#fff'}}>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px'}}>
                              <strong style={{color:'#1f2937'}}>Sinistre #{idx + 1}</strong>
                              <button type="button" onClick={() => removeSinistre(idx)} style={{background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:'13px'}}>
                                🗑️ Supprimer
                              </button>
                            </div>

                            <div className="vm-edit-grid">
                              <div className="vm-editable-field">
                                <label>Date du sinistre <span className="vm-field-badge">⚠ clé</span></label>
                                <input type="text" placeholder="JJ/MM/AAAA" value={s.dateSinistre ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'dateSinistre', e.target.value)}
                                  className="vm-input-important" />
                              </div>

                              <div className="vm-editable-field">
                                <label>Garantie <span className="vm-field-badge">⚠ clé</span></label>
                                <select value={s.garantie ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'garantie', e.target.value)}
                                  className="vm-select vm-input-important">
                                  <option value="">— choisir —</option>
                                  {GARANTIES_RI.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                                {s.garantieBrute && s.garantieBrute !== s.garantie && (
                                  <small style={{color:'#6b7280', fontSize:'11px'}}>PDF : "{s.garantieBrute}"</small>
                                )}
                              </div>

                              <div className="vm-editable-field">
                                <label>État</label>
                                <select value={s.etat ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'etat', e.target.value)}
                                  className="vm-select">
                                  <option value="">— choisir —</option>
                                  {ETATS_RI.map(et => <option key={et} value={et}>{et}</option>)}
                                </select>
                                {s.etatBrut && s.etatBrut !== s.etat && (
                                  <small style={{color:'#6b7280', fontSize:'11px'}}>PDF : "{s.etatBrut}"</small>
                                )}
                              </div>

                              <div className="vm-editable-field">
                                <label>N° dossier compagnie</label>
                                <input type="text" value={s.numeroDossierCompagnie ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'numeroDossierCompagnie', e.target.value)} />
                              </div>
                            </div>

                            <div className="vm-editable-field" style={{marginTop:'8px'}}>
                              <label>Circonstances / Description</label>
                              <textarea value={s.circonstances ?? ''}
                                onChange={(e) => updateSinistre(idx, 'circonstances', e.target.value)}
                                rows={2}
                                style={{width:'100%', padding:'6px 8px', border:'1px solid #e5e7eb', borderRadius:'4px', fontFamily:'inherit', fontSize:'13px', resize:'vertical'}} />
                            </div>

                            <div className="vm-edit-grid" style={{marginTop:'8px'}}>
                              <div className="vm-editable-field">
                                <label>Évaluations (€)</label>
                                <input type="text" value={s.evaluations ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'evaluations', e.target.value)} />
                              </div>
                              <div className="vm-editable-field">
                                <label>Règlements (€)</label>
                                <input type="text" value={s.reglements ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'reglements', e.target.value)} />
                              </div>
                              <div className="vm-editable-field">
                                <label>Honoraires (€)</label>
                                <input type="text" value={s.honoraires ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'honoraires', e.target.value)} />
                              </div>
                              <div className="vm-editable-field">
                                <label>Franchise (€)</label>
                                <input type="text" value={s.franchise ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'franchise', e.target.value)} />
                              </div>
                              <div className="vm-editable-field">
                                <label>Recours obtenu (€)</label>
                                <input type="text" value={s.recoursObtenu ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'recoursObtenu', e.target.value)} />
                              </div>
                              <div className="vm-editable-field">
                                <label>Coût total estimé (€) <span className="vm-field-badge">⚠ Airtable</span></label>
                                <input type="text" value={s.coutTotal ?? ''}
                                  onChange={(e) => updateSinistre(idx, 'coutTotal', e.target.value)}
                                  className="vm-input-important" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ─── Sections contrat / quittance (masquées si RI) ─── */}
                {!isRI(editData) && <>
                {/* Section Syndic */}
                <div className="vm-edit-section">
                  <h4>Syndic</h4>
                  <div className="vm-edit-grid">
                    <F k="nomSyndic" l="Nom" data={editData} onChange={handleInputChange} />
                    <F k="adresseSyndic" l="Adresse" imp data={editData} onChange={handleInputChange} />
                    <F k="cpSyndic" l="Code Postal" imp data={editData} onChange={handleInputChange} />
                    <F k="villeSyndic" l="Ville" imp data={editData} onChange={handleInputChange} />
                  </div>
                </div>

                {/* Section Copropriété */}
                <div className="vm-edit-section">
                  <h4>Copropriété / Immeuble</h4>
                  <div className="vm-edit-grid">
                    <F k="nomCopropriete" l="Nom copropriété" data={editData} onChange={handleInputChange} />
                    <F k="adresseImmeuble" l="Adresse immeuble" imp data={editData} onChange={handleInputChange} />
                    <F k="cpImmeuble" l="Code Postal" imp data={editData} onChange={handleInputChange} />
                    <F k="villeImmeuble" l="Ville" imp data={editData} onChange={handleInputChange} />
                  </div>
                </div>

                {/* Section Agent / Courtier */}
                <div className="vm-edit-section">
                  <h4>Agent / Courtier</h4>
                  <div className="vm-edit-grid">
                    <F k="nomAgent" l="Nom" imp data={editData} onChange={handleInputChange} />
                    <F k="adresseAgent" l="Adresse" data={editData} onChange={handleInputChange} />
                    <F k="cpAgent" l="Code Postal" data={editData} onChange={handleInputChange} />
                    <F k="villeAgent" l="Ville" data={editData} onChange={handleInputChange} />
                    <F k="telAgent" l="Téléphone" data={editData} onChange={handleInputChange} />
                    <F k="emailAgent" l="Email" data={editData} onChange={handleInputChange} />
                  </div>
                </div>

                {/* Section Contrat & Quittance */}
                <div className="vm-edit-section">
                  <h4>Contrat & Quittance</h4>
                  <div className="vm-edit-grid">
                    <F  k="primeTTC" l="Prime TTC (€)" data={editData} onChange={handleInputChange} />
                    <F  k="primeAnnuelle" l="Prime Annuelle (€)" data={editData} onChange={handleInputChange} />
                    <F  k="periodeDebut" l="Date Début" data={editData} onChange={handleInputChange} />
                    <F  k="periodeFin" l="Date Fin" data={editData} onChange={handleInputChange} />
                    <F  k="evolutionPrime" l="Évolution (%)" data={editData} onChange={handleInputChange} />
                    <F  k="echeanceAnnuelle" l="Échéance Annuelle (JJ/MM)" data={editData} onChange={handleInputChange} />
                    <F  k="dateSuspensionResiliation" l="Date Suspension/Résiliation" data={editData} onChange={handleInputChange} />
                    <F  k="indiceFFB" l="Indice FFB" data={editData} onChange={handleInputChange} />
                    <F  k="indiceFFBContrat" l="Indice FFB Contrat" data={editData} onChange={handleInputChange} />
                    <SF k="quittancement" l="Quittancement" data={editData} onChange={handleInputChange} />
                  </div>
                </div>

                {/* Section Immeuble */}
                <div className="vm-edit-section">
                  <h4>Caractéristiques du bâtiment</h4>
                  <div className="vm-edit-grid">
                    <F  k="nbBatiments" l="Nb Bâtiments" data={editData} onChange={handleInputChange} />
                    <F  k="surfaceTotale" l="Surface (m²)" data={editData} onChange={handleInputChange} />
                    <F  k="nbNiveaux" l="Nb Niveaux" data={editData} onChange={handleInputChange} />
                    <F  k="nbSousSol" l="Nb Sous-sols" data={editData} onChange={handleInputChange} />
                    <F  k="anneeConstruction" l="Année de construction" data={editData} onChange={handleInputChange} />
                    <SF k="usageBatiment" l="Usage du bâtiment" data={editData} onChange={handleInputChange} />
                    <SF k="typeToiture" l="Type de toiture" data={editData} onChange={handleInputChange} />
                    <SF k="commerce" l="Commerce (surface)" data={editData} onChange={handleInputChange} />
                    <F  k="typeCommerce" l="Type Commerce" data={editData} onChange={handleInputChange} />
                    <SF k="zoneInondable" l="Zone Inondable" data={editData} onChange={handleInputChange} />
                    <SF k="quartierPrioritaire" l="Quartier Prioritaire" data={editData} onChange={handleInputChange} />
                    <SF k="altitude" l="Altitude" data={editData} onChange={handleInputChange} />
                    <F  k="pourcentageMatDurs" l="% Matériaux durs" data={editData} onChange={handleInputChange} />
                    <SF k="tauxVide" l="% Vide d'occupant" data={editData} onChange={handleInputChange} />
                    <SF k="natureChauffage" l="Nature chauffage" data={editData} onChange={handleInputChange} />
                    <SF k="mh" l="Monument Historique" data={editData} onChange={handleInputChange} />
                    <F  k="travaux10Ans" l="Travaux (10 ans)" data={editData} onChange={handleInputChange} />
                  </div>
                  <div className="vm-edit-grid vm-edit-grid-full">
                    <CG k="typeChauffage" l="Type de chauffage" data={editData} onChange={handleInputChange} />
                    <CG k="installationEquipement" l="Installations / Équipements" data={editData} onChange={handleInputChange} />
                    <CG k="BrisMachine" l="Bris de machine" data={editData} onChange={handleInputChange} />
                  </div>
                </div>

                {/* Section Garanties et Franchises */}
                <div className="vm-edit-section">
                  <h4>Garanties et Franchises</h4>
                  <div className="vm-edit-grid">
                    <F k="garanties" l="Garanties" data={editData} onChange={handleInputChange} />
                    <F k="exclusions" l="Exclusions" data={editData} onChange={handleInputChange} />
                    <F k="franchiseGenerale" l="Franchise Générale (€)" data={editData} onChange={handleInputChange} />
                    <F k="franchisesSpecifiques" l="Franchises Spécifiques" data={editData} onChange={handleInputChange} />
                    <F k="lci" l="Limite Contractuelle d'Indemnité (LCI en €):" data={editData} onChange={handleInputChange} />
                  </div>
                </div>
                </>}
                {/* /fin sections non-RI ─────────────────────────────────────── */}

              </div>

              <div className="vm-edit-footer">
                <button className="vm-btn-secondary" onClick={handleCancel}>Annuler</button>
                <button className="vm-btn-primary" onClick={handleSave}>Enregistrer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ValidationModal;
